import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { UploadCloud, Loader2, X } from "lucide-react";
import { adminUploadImage } from "@/lib/api";
import { cn } from "@/lib/utils";

/**
 * Drag-and-drop image uploader. Uploads files one-by-one to the backend and
 * calls `onUrls` with the array of returned URLs.
 */
export const ImageUploader = ({ creds, onUrls, disabled = false }) => {
  const inputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [queue, setQueue] = useState([]); // { name, progress, status }

  const uploadFiles = async (files) => {
    const arr = Array.from(files);
    if (arr.length === 0) return;

    const jobs = arr.map((f, i) => ({ id: `${Date.now()}-${i}`, name: f.name, progress: 0, status: "uploading" }));
    setQueue((q) => [...q, ...jobs]);

    const urls = [];
    for (let i = 0; i < arr.length; i++) {
      const f = arr[i];
      const jobId = jobs[i].id;
      try {
        const res = await adminUploadImage(creds, f, (pct) => {
          setQueue((q) => q.map((j) => (j.id === jobId ? { ...j, progress: pct } : j)));
        });
        urls.push(res.url);
        setQueue((q) => q.map((j) => (j.id === jobId ? { ...j, status: "done", progress: 100 } : j)));
      } catch (err) {
        setQueue((q) => q.map((j) => (j.id === jobId ? { ...j, status: "error" } : j)));
        toast.error(`Upload failed: ${f.name}`, { description: err?.response?.data?.detail || "Try a smaller image (< 10 MB)." });
      }
    }

    if (urls.length > 0) {
      onUrls(urls);
      toast.success(`Uploaded ${urls.length} image${urls.length === 1 ? "" : "s"}`);
    }

    // Remove completed jobs after 2s
    setTimeout(() => {
      setQueue((q) => q.filter((j) => j.status === "uploading"));
    }, 2000);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (disabled) return;
    uploadFiles(e.dataTransfer.files);
  };

  const onDragOver = (e) => {
    e.preventDefault();
    if (!disabled) setDragActive(true);
  };

  const onDragLeave = (e) => {
    e.preventDefault();
    setDragActive(false);
  };

  return (
    <div>
      <div
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onClick={() => !disabled && inputRef.current?.click()}
        className={cn(
          "border-2 border-dashed rounded-sm p-6 cursor-pointer transition-colors text-center",
          dragActive ? "border-primary bg-primary/5" : "border-border hover:border-primary/50 hover:bg-muted/30",
          disabled && "opacity-50 pointer-events-none"
        )}
        data-testid="admin-image-dropzone"
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
          className="hidden"
          onChange={(e) => uploadFiles(e.target.files)}
          data-testid="admin-image-file-input"
        />
        <UploadCloud className="w-8 h-8 mx-auto text-secondary" strokeWidth={1.5} />
        <p className="mt-3 font-serif text-lg">Drag &amp; drop images here</p>
        <p className="mt-1 text-xs text-muted-foreground">or click to browse. JPG, PNG, WEBP or GIF. Max 10&nbsp;MB per file.</p>
      </div>

      {queue.length > 0 && (
        <div className="mt-3 space-y-2" data-testid="admin-upload-queue">
          {queue.map((j) => (
            <div key={j.id} className="flex items-center gap-3 border border-border p-2 text-xs">
              {j.status === "uploading" && <Loader2 className="w-4 h-4 animate-spin text-secondary" />}
              {j.status === "done" && <span className="w-4 h-4 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-[10px]">✓</span>}
              {j.status === "error" && <X className="w-4 h-4 text-destructive" />}
              <span className="flex-1 truncate">{j.name}</span>
              <div className="w-24 h-1.5 bg-muted rounded overflow-hidden">
                <div className={cn("h-full transition-all", j.status === "error" ? "bg-destructive" : "bg-primary")} style={{ width: `${j.progress}%` }} />
              </div>
              <span className="w-10 text-right tabular-nums">{j.progress}%</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
